import { SxProps, Theme } from '@mui/material';
import { OpenSansFontWeights } from 'shared/custom/constants/commonConstants';

type PromoIconSX = {
	remate: SxProps<Theme>;
	gr: SxProps<Theme>;
	sb: SxProps<Theme>;
	nlp: SxProps<Theme>;
	combo: SxProps<Theme>;
	fp: SxProps<Theme>;
	fpr: SxProps<Theme>;
	nxm: SxProps<Theme>;
	ahp: SxProps<Theme>;
	cp: SxProps<Theme>;
	ce: SxProps<Theme>;
	msi: SxProps<Theme>;
	[key: string]: SxProps<Theme>;
};

export const promoIconSX: PromoIconSX = {
	remate: {
		width: 78,
		height: 35,
		marginRight: '5px',
	},
	gr: {
		width: 49,
		height: 22,
	},
	sb: {
		width: 37,
		height: 24,
	},
	nlp: {
		width: 30,
		height: 30,
	},
	combo: {
		width: 81,
		height: 35,
		marginRight: '5px',
	},
	fp: {
		width: 64,
		height: 35,
		marginRight: '5px',
	},
	fpr: {
		width: 48,
		height: 48,
		marginRight: '5px',
	},
	nxm: {
		width: 'auto',
		height: 35,
		marginRight: '5px',
	},
	ahp: {
		width: '60px',
		height: '35px',
		marginRight: '5px',
	},
	cp: {
		width: '44px',
		height: '35px',
		marginRight: '5px',
	},
	ce: {
		width: '53px',
		height: '46px',
		marginRight: '5px',
	},
	msi: {
		width: '51px',
		height: '35px',
		marginRight: '10px',
	},
};

export const msiSX = {
	container: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		cursor: 'pointer',
		paddingBottom: '10px',
	},
	iconContainer: {
		flexShrink: 0,
	},
	textContainer: {
		flex: 1,
	},
	titleContainer: (theme) => ({
		fontFamily: '"HelveticaNeue_THD", Open Sans, Helvetica Neue, Arial, sans-serif',
		display: 'flex',
		flexDirection: 'column',
		gap: 0,
		textTransform: 'uppercase',
		fontWeight: OpenSansFontWeights.bold,
		whiteSpace: 'nowrap',
		[theme.breakpoints.up('md')]: {
			gap: '5px',
			flexDirection: 'row',
		},
	}),
	msiText: {
		fontFamily: 'inherit',
		textTransform: 'inherit',
		fontWeight: 'inherit',
	},
	availableText: {
		fontFamily: 'inherit',
		color: 'text.highlight',
		textTransform: 'inherit',
		fontWeight: 'inherit',
	},
	priceText: {
		fontWeight: OpenSansFontWeights.semibold,
	},
	monthsTextContainer: {
		ml: 0.5,
		fontWeight: OpenSansFontWeights.regular,
	},
	perMonthsText: {
		fontWeight: 'inherit',
	},
	monthsText: {
		color: 'text.highlight',
		fontWeight: 'inherit',
	},
	seeOptions: {
		ml: 0.5,
		color: 'secondary.main',
		cursor: 'pointer',
	},
	chevronIconContainer: {
		flexShrink: 0,
	},
	chevronIcon: {
		width: '24px',
		height: '24px',
		mt: '6px',
		cursor: 'pointer',
	},
	menuIcon: {
		color: 'red.500',
	},
	msiEspot: {
		margin: '10px',
	},
} as const satisfies Record<string, SxProps<Theme>>;

export const promoGeneralSX: Record<string, SxProps<Theme>> = {
	promoiconContainer: {
		height: 22,
		display: 'flex',
		justifyContent: 'left',
		alignItems: 'center',
		marginBottom: 1,
	},
};

export const remateSX: Record<string, SxProps<Theme>> = {
	saveMoreSubcontainer: {
		width: '75%',
		marginRight: 10,
	},
};

export const freeGiftThemeSX: Record<string, SxProps<Theme>> = {
	container: {
		outline: '1px solid',
		outlineColor: 'secondary.main',
		borderRadius: '3px',
		display: 'flex',
		'&:hover': {
			outlineColor: 'primary.main',
		},
		marginBottom: '10px',
	},
	radioContainer: {
		height: '90px',
		width: '60px',
		minWidth: '60px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: 'grey.100',
		borderRadius: '3px',
		borderBottomRightRadius: 0,
		borderTopRightRadius: 0,
		fontSize: 20,
	},
	radioContainerSelected: {
		backgroundColor: 'primary.main',
		color: 'white',
	},
	productTitleText: {
		height: 38,
		overflow: 'hidden',
		width: { xs: '150', sm: '200', md: '100%' },
	},
	freeGiftSku: {
		color: 'grey.300',
	},
	productContainer: {
		display: 'flex',
		p: '10px',
		height: '90px',
		alignItems: 'center',
		justifyContent: 'flex-start',
	},
	imageContainer: {
		minWidth: '60px',
		minHeight: '60px',
	},
	link: {
		color: 'secondary.main',
		textDecoration: 'none',
		'&:hover': { textDecoration: 'underline' },
	},
	linkText: { color: 'secondary.main', cursor: 'pointer' },
	detailsContainer: {
		display: 'flex',
		flexDirection: 'column',
		ml: '5px',
	},
};

export const freeGiftSX: Record<string, SxProps<Theme>> = {
	padding: {
		p: '10px',
	},
	container: {
		display: 'flex',
		height: '150px',
		alignItems: 'center',
		justifyContent: 'center',
	},
	plusIcon: {
		fontSize: '24px',
		color: 'grey.200',
		mx: '5px',
	},
	productContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	promoiconContainer: {
		display: 'flex',
		justifyContent: 'flex-start',
		alignItems: 'center',
		mb: 2,
	},
	radioContainer: {
		display: 'flex',
		width: '60px !important',
		minWidth: '60px !important',
		height: '90px',
		alignItems: 'center',
		justifyContent: 'center',
	},
	listFreeGiftImage: {
		width: 60,
		height: 60,
	},
	freeProductContainer: {
		minHeight: 50,
		display: 'flex',
		justifyContent: 'left',
		alignItems: 'center',
		marginTop: 10,
		marginBottom: 10,
	},
	freeProductSubContainer: {
		flexGrow: 1,
	},
	freeGiftText: {
		marginLeft: 10,
	},
	applyContainer: {
		minHeight: 50,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		marginLeft: 10,
	},
	aplicarText: {
		cursor: 'pointer',
	},
	dialogQualification: {
		padding: 10,
	},
	dialogSaveMoreContainer: {
		display: 'flex',
		height: 40,
	},
	dialogClose: {
		height: 40,
		display: 'flex',
		justifyContent: 'flex-end',
		paddingRight: 10,
	},
	selectedFreeGiftImage: {
		width: 90,
		height: 90,
	},
	drawerPaperSx: {
		'.MuiDrawer-paper': {
			width: { md: '100%', lg: 360 },
			maxHeight: { xs: '80vh', lg: '100%' },
			bottom: '60px',
		},
	},
	headerSx: {
		display: 'flex',
		justifyContent: 'space-between',
		p: '15px 20px',
		textAlign: 'left',
	},
	titleSx: {
		width: '100%',
	},
	closeButtonSx: {
		color: 'grey.200',
		cursor: 'pointer',
		fontSize: '20px',
		':hover': {
			color: 'primary.main',
		},
	},
	seePromotions: {
		display: 'block',
		textAlign: 'center',
		mt: '10px',
	},
	link: {
		textDecoration: 'none',
	},
	linkText: {
		color: 'secondary.main',
		cursor: 'pointer',
	},
};

export const drawerThemeSX: Record<string, (theme: Theme) => SxProps<Theme>> = {
	headerMenuText: () => ({
		lineHeight: 1.2,
		flex: 1,
		textAlign: 'center',
		marginRight: '40px',
		color: 'grey.500',
	}),
	conContClose: ({ palette, spacing }) => ({
		color: palette.grey[100],
		cursor: 'pointer',
		margin: spacing(1),
		fontSize: 25,
	}),
};

export const drawerSX: Record<string, SxProps<Theme>> = {
	closeIcon: {
		'&:hover': {
			color: 'primary.main',
		},
	},
	iconContCheckout: {
		width: 20,
		height: 20,
	} as SxProps<Theme>,
	headerMenu: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: '5px',
	},
	headerMenuH6: {
		marginLeft: 5,
		paddingLeft: 5,
		fontWeight: '300!important',
	},
	iconContBase: {
		display: 'flex',
		alignItems: 'center',
		color: 'grey.50',
		cursor: 'pointer',
	},
	drawerHomedepotPrologo: {
		width: 40,
		height: 40,
	},
	content: {
		width: '100%',
		height: '100%',
		zIndex: '-1',

		fixed: {
			overflowY: 'hidden',
		},
	},
	basicSeparator: {
		paddingBottom: '10px',
	},
	drawerContent: {
		borderTop: '2 solid #F96302',
		width: 300,
		zIndex: '-1',
	},
	drawer: {
		'.MuiDrawer-paper': {
			maxHeight: { sm: '100vh', lg: '100%' },
			bottom: '60px',
		},
	},
};

export const saveMoreSX = {
	container: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
	},
	subContainer: {
		flexGrow: 1,
	},
	promoIconContainer: (theme: Theme) => ({
		mb: 1,
		width: '100%',
		[theme.breakpoints.down('md')]: {
			width: '100%',
		},
	}),
	promoIcon: {
		width: '44px',
		height: '35px',
	},
	promoText: {
		ml: 0,
	},
	applyContainer: {
		ml: 2,
		mr: 2,
		textAlign: 'center',
	},
	applyText: (theme: Theme) => ({
		cursor: 'pointer',
		color: theme.palette.secondary.main,
		whiteSpace: 'nowrap',
		pr: 1,
	}),
	dialogSectionThree: {
		p: 2,
		textAlign: 'center',
	},
	dialogLink: (theme: Theme) => ({
		color: theme.palette.secondary.main,
		cursor: 'pointer',
		textDecoration: 'underline',
	}),
	divider: (theme: Theme) => ({
		backgroundColor: theme.palette.grey['50'],
		mx: 1,
	}),
};

export const saveMoreDialogSX = {
	section: {
		p: 2,
		borderBottom: (theme: Theme) => `1px solid ${theme.palette.grey[100]}`,
	},
	sectionHeader: {
		display: 'flex',
		alignItems: 'center',
		mb: 2,
	},
	numberAvatar: (theme: Theme) => ({
		width: 24,
		height: 24,
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		mr: 1,
	}),
	productContainer: {
		display: 'flex',
		alignItems: 'flex-start',
	},
	productImage: {
		width: 80,
		height: 80,
		mr: 2,
		objectFit: 'contain',
	},
	productInfo: {
		flex: 1,
	},
	productTitle: {
		display: 'inline',
	},
	skuText: {
		color: 'text.secondary',
	},
	actionsContainer: {
		display: 'flex',
		mt: 2,
		height: 50,
		alignItems: 'flex-end',
	},
	addToCartContainer: {
		ml: 1,
		width: '100%',
	},
};

export const separatorSx: SxProps<Theme> = {
	my: '15px',
};

export const verticalSeparatorSx: SxProps<Theme> = {
	width: '2px',
	mx: 2,
};

export const typoSX: Record<string, SxProps<Theme>> = {
	firstRow: (theme: Theme) => ({
		color: theme.palette.grey[500],
		fontWeight: 600,
		mb: '2px',
	}),
	secondRowBold: (theme: Theme) => ({
		color: theme.palette.grey[500],
		fontWeight: 600,
	}),
	secondRow: (theme: Theme) => ({
		color: theme.palette.grey[500],
		fontWeight: 400,
	}),
};
